import type { ReactNode } from 'react'

import { Avatar, AvatarFallback, AvatarImage, Checkbox } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

export interface PickerStudent {
  id: string
  name: string
  image?: string | null
  /** Secondary line under the name — grade and class for a school, nothing for a family. */
  subtitle?: string | null
}

export interface StudentPickerListProps {
  /** Already filtered by the caller. Select-all applies to exactly this list. */
  students: PickerStudent[]
  selected: string[]
  onSelectedChange: (next: string[]) => void
  disabled?: boolean
  emptyLabel?: string
  /** Rows rendered checked-and-locked. Excluded from select-all. */
  disabledIds?: string[]
  /** Right-aligned note on a row, e.g. a reason chip for a locked student. */
  rowNote?: (student: PickerStudent) => ReactNode
}

/** Select-all header plus a scrollable list of student checkbox rows. */
export function StudentPickerList({
  students,
  selected,
  onSelectedChange,
  disabled,
  emptyLabel = 'Tidak ada hasil yang ditemukan',
  disabledIds,
  rowNote,
}: StudentPickerListProps) {
  const lockedIds = new Set(disabledIds ?? [])
  const selectableStudents = students.filter((s) => !lockedIds.has(s.id))

  const selectedVisibleCount = selectableStudents.filter((s) => selected.includes(s.id)).length
  const allSelected = selectableStudents.length > 0 && selectedVisibleCount === selectableStudents.length

  const toggleAll = () =>
    onSelectedChange(
      allSelected
        ? selected.filter((id) => !selectableStudents.some((s) => s.id === id))
        : [...new Set([...selected, ...selectableStudents.map((s) => s.id)])],
    )

  return (
    <section className='srd:flex srd:w-full srd:flex-col srd:gap-y-2'>
      {/* A text action rather than a header checkbox: the rows already carry checkboxes, and a
          third one sitting above them read as "the list itself" instead of "all of them". */}
      <button
        type='button'
        className={cn(
          typographyVariants({ variant: 'label-md' }),
          'srd:text-blue-100 srd:self-end srd:disabled:opacity-50',
        )}
        disabled={disabled || selectableStudents.length === 0}
        onClick={toggleAll}
      >
        {allSelected ? 'Batal pilih semua' : 'Pilih semua'}
      </button>

      {/* `overflow-x-hidden` is not cosmetic: `overflow-y-auto` alone computes the X axis to `auto`
          too, and `Checkbox` carries an absolutely positioned `after:-inset-x-3` hit-area halo that
          overhangs the row by 12px. That overhang counts toward scrollable overflow, so the list grew
          a horizontal scrollbar with nothing to scroll to. `min-w-0` also opts out of the UA
          `fieldset { min-inline-size: min-content }` rule, which otherwise refuses to shrink. */}
      <fieldset
        className='srd:flex srd:max-h-[45dvh] srd:w-full srd:min-w-0 srd:flex-col srd:overflow-x-hidden srd:overflow-y-auto'
        disabled={disabled}
      >
        {students.length === 0 ? (
          <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100 srd:py-8 srd:text-center')}>
            {emptyLabel}
          </p>
        ) : (
          students.map((student, index) => {
            const isLocked = lockedIds.has(student.id)
            const note = rowNote?.(student)

            return (
              <label
                key={student.id}
                className={cn(
                  'srd:border-grey-30 srd:flex srd:w-full srd:items-center srd:gap-x-4 srd:border-t srd:py-2 srd:pr-3 srd:pl-2 srd:select-none',
                  index === students.length - 1 && 'srd:border-b',
                  isLocked ? 'srd:cursor-not-allowed srd:opacity-60' : 'srd:cursor-pointer',
                )}
              >
                {/* `flex-1 min-w-0`, never `w-full`: at 100% of the row this block claimed the full
                    content box and then pushed the gap and the checkbox past it, so the scroll
                    container reported horizontal overflow on every row. */}
                <div className='srd:flex srd:min-w-0 srd:flex-1 srd:items-center srd:gap-x-2'>
                  <Avatar profile='user' className='srd:size-8'>
                    <AvatarImage src={student.image ?? undefined} alt={student.name} />
                    <AvatarFallback>{getInitials(student.name)}</AvatarFallback>
                  </Avatar>
                  <div className='srd:flex srd:min-w-0 srd:flex-1 srd:flex-col'>
                    <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:line-clamp-2')}>
                      {student.name}
                    </p>
                    {student.subtitle ? (
                      <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:truncate')}>
                        {student.subtitle}
                      </p>
                    ) : null}
                  </div>
                </div>

                {note ? <span className='srd:shrink-0'>{note}</span> : null}

                <Checkbox
                  checked={isLocked || selected.includes(student.id)}
                  disabled={isLocked}
                  onCheckedChange={(checked) => {
                    if (isLocked) return
                    onSelectedChange(checked ? [...selected, student.id] : selected.filter((id) => id !== student.id))
                  }}
                  className='srd:shrink-0'
                />
              </label>
            )
          })
        )}
      </fieldset>
    </section>
  )
}
